import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Avatar,
  Typography,
  TextField,
  MenuItem,
  Button,
  Alert,
  Stack,
  Divider,
} from "@mui/material";
import {
  SearchRounded,
  RestartAltRounded,
  PictureAsPdfRounded,
  TableChartRounded,
} from "@mui/icons-material";
import { format } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { MRT_SortingState } from "material-react-table";

import { DefaultPage } from "../../layouts/default-page";
import CustomConfirmDialog from "../../layouts/confirm_dialogue";
import CustomInputDialog from "../../layouts/input_dialogue";
import { Notify } from "../../layouts/notify";
import {
  useApproveUserMutation,
  useDeclineUserMutation,
  useGetUnVerifiedDeputyUsersQuery,
} from "../../services/user.query";
import { DataTableFive } from "../../layouts/layout2/data-table-five";
import { StatusChip } from "../../components/common/StatusChip";
import { RowActions } from "../../components/common/RowActions";
import { ExportLoadingBackdrop } from "../../components/common/ExportLoadingBackdrop";
import { useTableState } from "../../hooks/useTableState";

export default function VerificationCenter() {
  const defaultFilters = useMemo(
    () => ({
      status: "PENDING" as "PENDING" | "REJECTED",
      search: "",
    }),
    []
  );

  const {
    page,
    pageSize,
    sortBy,
    sortOrder,
    filters,
    hasActiveFilters,
    setPage,
    setSorting: updateTableSorting,
    setFilters,
    resetFilters,
  } = useTableState(defaultFilters, {
    defaultPage: 1,
    defaultPageSize: 100,
    defaultSortBy: "created_at",
    defaultSortOrder: "desc",
  });

  const [draftStatus, setDraftStatus] = useState<"PENDING" | "REJECTED">(filters.status);
  const [draftSearch, setDraftSearch] = useState(filters.search);

  useEffect(() => {
    setDraftStatus(filters.status);
    setDraftSearch(filters.search);
  }, [filters]);

  const sorting = useMemo<MRT_SortingState>(
    () => [{ id: sortBy, desc: sortOrder === "desc" }],
    [sortBy, sortOrder]
  );

  const [modalOne, setModalOne] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [modalTwo, setModalTwoState] = useState<boolean>(false);

  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState("");

  const [declineUser, { isLoading: declining }] = useDeclineUserMutation();
  const [approveUser, { isLoading: approving }] = useApproveUserMutation();

  const {
    data: users = [],
    isLoading,
    isFetching,
    isError,
  } = useGetUnVerifiedDeputyUsersQuery({ status: filters.status });

  const filteredData = useMemo(() => {
    let result = users;

    if (filters.search) {
      const keyword = filters.search.toLowerCase();
      result = result.filter((pr: any) => {
        const searchContent = `${pr?.name || ""} ${pr?.last_name || ""} ${pr?.email || ""} ${pr?.phone_number || ""}`.toLowerCase();
        return searchContent.includes(keyword);
      });
    }

    if (sorting.length > 0) {
      const { id, desc } = sorting[0];
      result = [...result].sort((a: any, b: any) => {
        let valA = a[id];
        let valB = b[id];

        if (id === "name") {
          valA = `${a.last_name || ""}, ${a.name || ""}`;
          valB = `${b.last_name || ""}, ${b.name || ""}`;
        }

        if (valA < valB) return desc ? 1 : -1;
        if (valA > valB) return desc ? -1 : 1;
        return 0;
      });
    }

    return result;
  }, [users, filters.search, sorting]);

  const totalCount = filteredData.length;
  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, page, pageSize]);

  const handleSearch = () => {
    setFilters({
      status: draftStatus,
      search: draftSearch.trim(),
    });
  };

  const handleClear = () => {
    resetFilters();
  };

  const handleEnterSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const openModalOne = (row: any) => {
    setModalOne(true);
    setSelectedUser(row);
  };

  const approveItem = async () => {
    try {
      await approveUser(selectedUser?.id);
      Notify("success", "Verified Successfully");
      setModalOne(false);
    } catch (err) {
      setModalOne(false);
      Notify("error", "Error Happened while Approving");
    }
  };

  const openModalTwo = (row: any) => {
    setModalTwoState(true);
    setSelectedUser(row);
  };

  const rejectItem = async (reason: string) => {
    try {
      await declineUser({ id: selectedUser?.id, reasonStr: reason });
      Notify("error", "User Rejected Successfully");
      setModalTwoState(false);
    } catch (err) {
      setModalTwoState(false);
      Notify("error", "Error happened while declining");
    }
  };

  const handleExportPDF = async () => {
    if (filteredData.length === 0) {
      Notify("info", "No records found to export.");
      return;
    }

    setIsExporting(true);
    setExportMessage(`Preparing PDF for all ${filteredData.length.toLocaleString()} registrations...`);
    await new Promise((resolve) => setTimeout(resolve, 50));

    try {
      const doc = new jsPDF("landscape", "mm", "a4");
      doc.setFontSize(14);
      doc.setTextColor(0, 114, 188);
      doc.text("MTRCB - Public User Registration Verification List", 14, 15);

      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(
        `Generated on: ${format(new Date(), "MMMM dd, yyyy · hh:mm a")} | Filter: Status = ${filters.status} | Total Records: ${filteredData.length}`,
        14,
        22
      );

      const tableColumn = ["#", "Registration Date", "Name", "Email", "Mobile #", "Status"];
      const tableRows = filteredData.map((item: any, idx: number) => [
        idx + 1,
        item.created_at ? format(new Date(item.created_at), "MM/dd/yyyy") : "—",
        `${item.last_name || ""}, ${item.name || ""}`.trim() || "—",
        item.email || "—",
        item.phone_number || "—",
        item.status || "PENDING",
      ]);

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 27,
        theme: "grid",
        headStyles: {
          fillColor: [0, 114, 188],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 8.5,
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        styles: {
          fontSize: 8,
          cellPadding: 2.5,
          textColor: [15, 23, 42],
          overflow: "linebreak",
        },
        margin: { top: 12, left: 14, right: 14, bottom: 12 },
        didDrawPage: (data: any) => {
          const pageCount = (doc as any).internal.getNumberOfPages();
          doc.setFontSize(7.5);
          doc.setTextColor(148, 163, 184);
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            doc.internal.pageSize.getWidth() - 30,
            doc.internal.pageSize.getHeight() - 6
          );
        },
      });

      doc.save(`MTRCB_Registrations_${filters.status}_${format(new Date(), "yyyyMMdd_HHmm")}.pdf`);
      Notify("success", `Exported all ${filteredData.length} records to PDF.`);
    } catch (err: any) {
      console.error("PDF export error:", err);
      Notify("error", err?.message || "Failed to generate PDF export.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    if (filteredData.length === 0) {
      Notify("info", "No records found to export.");
      return;
    }

    setIsExporting(true);
    setExportMessage(`Generating Excel spreadsheet for ${filteredData.length.toLocaleString()} registrations...`);
    await new Promise((resolve) => setTimeout(resolve, 50));

    try {
      const rows = filteredData.map((item: any, idx: number) => ({
        "No.": idx + 1,
        "Registration Date": item.created_at ? format(new Date(item.created_at), "yyyy-MM-dd") : "—",
        "Last Name": item.last_name || "—",
        "First Name": item.name || "—",
        "Email Address": item.email || "—",
        "Mobile Number": item.phone_number || "—",
        "Status": item.status || "PENDING",
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Registrations");

      ws["!cols"] = [
        { wch: 6 },
        { wch: 18 },
        { wch: 20 },
        { wch: 20 },
        { wch: 30 },
        { wch: 18 },
        { wch: 14 },
      ];

      XLSX.writeFile(wb, `MTRCB_Registrations_${filters.status}_${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`);
      Notify("success", `Exported all ${filteredData.length} records to Excel.`);
    } catch (err: any) {
      console.error("Excel export error:", err);
      Notify("error", err?.message || "Failed to generate Excel export.");
    } finally {
      setIsExporting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "number",
        header: "No.",
        size: 70,
        enableSorting: false,
        Cell: ({ row }: any) => (
          <Typography>{(page - 1) * pageSize + row.index + 1}</Typography>
        ),
      },
      {
        accessorKey: "created_at",
        header: "Registration Date",
        size: 160,
        Cell: ({ cell }: any) => cell.getValue() ? format(new Date(cell.getValue()), "MM/dd/yyyy") : "—",
      },
      {
        accessorKey: "name",
        header: "User",
        size: 260,
        Cell: ({ row }: any) => {
          const { profile_picture, name, last_name } = row.original;
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar
                src={
                  profile_picture ||
                  "https://smrgejlacnpeerpzxezv.supabase.co/storage/v1/object/public/assets/default-user-image.png"
                }
                sx={{ width: 35, height: 35 }}
              />
              <Typography>
                {last_name}, {name}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: "gov_issued_ID_url",
        header: "ID",
        size: 100,
        enableSorting: false,
        Cell: ({ row }: any) => {
          const imageUrl = row.original.gov_issued_ID_url;
          return imageUrl ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <img
                src={imageUrl}
                alt="ID"
                style={{ width: 45, height: 30, cursor: "pointer", objectFit: "cover", borderRadius: 4, border: "1px solid #ccc" }}
                onClick={() => window.open(imageUrl, "_blank")}
              />
            </Box>
          ) : (
            <Typography color="text.secondary">Empty</Typography>
          );
        },
      },
      {
        accessorKey: "email",
        header: "Email",
        size: 240,
      },
      {
        accessorKey: "phone_number",
        header: "Mobile #",
        size: 160,
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 130,
        Cell: ({ cell }: any) => {
          const status = cell.getValue() || "PENDING";
          return <StatusChip label={status} />;
        },
      },
      {
        id: "actions",
        header: "Actions",
        size: 160,
        enableSorting: false,
        Cell: ({ row }: any) => {
          const status = row.original.status;

          return (
            <RowActions
              actions={[
                {
                  label: "Approve",
                  onClick: () => openModalOne(row.original),
                  hidden: status !== "PENDING",
                },
                {
                  label: "Reject",
                  tone: "error",
                  onClick: () => openModalTwo(row.original),
                  hidden: status !== "PENDING",
                },
              ]}
            />
          );
        },
      },
    ],
    [page, pageSize]
  );

  return (
    <DefaultPage 
      title="Public User Registrations" 
      subTitle="Verify and manage new public user registrations."
      loading={isLoading}
    >
      <Box mb={2.5}>
        <Alert
          severity="info"
          sx={{
            borderRadius: "6px",
            fontSize: 13,
            border: "1px solid #BAE6FD",
            backgroundColor: "#F0F9FF",
            "& .MuiAlert-icon": { color: "#0072BC" },
          }}
        >
          Displays new public user registrations that require verification. Set filters and click <strong>Search</strong> to load results.
        </Alert>
      </Box>

      {isError && (
        <Box mb={2.5}>
          <Alert severity="error" sx={{ borderRadius: "6px", fontSize: 13 }}>
            Failed to load user registrations.
          </Alert>
        </Box>
      )}

      {/* Standardized Gold-Standard Filter Card */}
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: "6px",
          p: 2,
          mb: 2,
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04)",
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <TextField
              select
              size="small"
              label="Status"
              value={draftStatus}
              onChange={(e) => setDraftStatus(e.target.value as "PENDING" | "REJECTED")}
              sx={{
                minWidth: 160,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            >
              <MenuItem value="PENDING">PENDING</MenuItem>
              <MenuItem value="REJECTED">REJECTED</MenuItem>
            </TextField>

            <TextField
              size="small"
              label="Search User / Email / Phone"
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
              onKeyDown={handleEnterSearch}
              sx={{
                minWidth: 280,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            />
          </Stack>

          <Divider sx={{ borderColor: "#F1F5F9" }} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<SearchRounded fontSize="small" />}
                onClick={handleSearch}
                disabled={isFetching}
                sx={{
                  backgroundColor: "#0072BC",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  fontSize: 13,
                  px: 2,
                  py: 0.75,
                  borderRadius: "4px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#005a96",
                    boxShadow: "none",
                  },
                }}
              >
                {isFetching ? "Searching..." : "Search"}
              </Button>

              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RestartAltRounded fontSize="small" />}
                  onClick={handleClear}
                  disabled={isFetching}
                  sx={{
                    color: "#64748B",
                    borderColor: "#CBD5E1",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: "4px",
                    "&:hover": {
                      borderColor: "#94A3B8",
                      backgroundColor: "#F8FAFC",
                    },
                  }}
                >
                  Clear
                </Button>
              )}
            </Stack>

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdfRounded fontSize="small" />}
                onClick={handleExportPDF}
                disabled={isExporting}
                sx={{
                  color: "#334155",
                  borderColor: "#CBD5E1",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#94A3B8",
                    backgroundColor: "#F8FAFC",
                  },
                }}
              >
                Export PDF
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<TableChartRounded fontSize="small" />}
                onClick={handleExportExcel}
                disabled={isExporting}
                sx={{
                  color: "#0072BC",
                  borderColor: "#BAE6FD",
                  backgroundColor: "#F0F9FF",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#0072BC",
                    backgroundColor: "#E0F2FE",
                  },
                }}
              >
                Export Excel
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      {/* Matched Records Banner */}
      <Box mb={2}>
        <Alert
          severity="success"
          sx={{
            borderRadius: "6px",
            fontSize: 13,
            border: "1px solid #BBF7D0",
            backgroundColor: "#F0FDF4",
            py: 0.5,
            "& .MuiAlert-icon": { color: "#00934C" },
          }}
        >
          Total matched records: <strong>{Number(totalCount || 0).toLocaleString()}</strong>
        </Alert>
      </Box>

      <DataTableFive
        data={paginatedData}
        total={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        columns={columns as any}
        getRowId={(row: any) => row.id}
        isLoading={isLoading}
        isFetching={isFetching}
        onRowClick={(row: any) => {
          if (row.original.status === "PENDING") {
            openModalOne(row.original);
          }
        }}
        sorting={sorting}
        onSortingChange={(updater: any) => {
          const next = typeof updater === "function" ? updater(sorting) : updater;
          if (next && next.length > 0) {
            updateTableSorting(next[0].id, next[0].desc ? "desc" : "asc");
          }
        }}
      />

      <ExportLoadingBackdrop open={isExporting} message={exportMessage} />

      <CustomConfirmDialog
        open={modalOne}
        onClose={() => setModalOne(false)}
        onConfirm={approveItem}
        loading={approving}
        title={`Approve ${selectedUser?.name}?`}
        description={`Do you really want to approve ${selectedUser?.name}?`}
        color="success"
      />

      <CustomInputDialog
        open={modalTwo}
        onClose={() => setModalTwoState(false)}
        onSubmit={rejectItem}
        loading={declining}
        title={`Reject ${selectedUser?.name}?`}
        description={`Do you really want to reject ${selectedUser?.name}?\nPlease provide a reason for rejection.`}
        color="error"
      />
    </DefaultPage>
  );
}
